<template>
  <div v-bind="$attrs" class="ui-flex" :style="resolvedStyle"><slot /></div>
</template>

<script setup lang="ts">
  import { type CSSProperties, computed } from 'vue'

  defineOptions({ inheritAttrs: false, name: 'UiFlex' })
  type Align =
    | 'around'
    | 'between'
    | 'bottom'
    | 'center'
    | 'evenly'
    | 'left'
    | 'right'
    | 'stretch'
    | 'top'
  type Direction = 'column' | 'column-reverse' | 'row' | 'row-reverse'

  const props = withDefaults(
    defineProps<{
      alignX?: Align
      alignY?: Align
      bgColor?: string
      direction?: Direction
      flex?: 'content' | 'fluid'
      gap?: number
      height?: number | string
      overflowY?: 'auto' | 'hidden' | 'scroll' | 'visible'
      padding?: number
      paddingX?: number
      paddingY?: number
      radius?: number
      reverse?: boolean
      width?: number | string
      wrap?: 'no-wrap' | 'wrap' | 'wrap-reverse'
    }>(),
    { direction: 'row' },
  )

  const dimension = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value)
  const alignment: Record<Align, string> = {
    around: 'space-around',
    between: 'space-between',
    bottom: 'flex-end',
    center: 'center',
    evenly: 'space-evenly',
    left: 'flex-start',
    right: 'flex-end',
    stretch: 'stretch',
    top: 'flex-start',
  }
  const flexDirection = computed(() => {
    if (!props.reverse) return props.direction
    return props.direction.endsWith('-reverse')
      ? props.direction.replace('-reverse', '')
      : `${props.direction}-reverse`
  })
  const isColumn = computed(() => flexDirection.value.startsWith('column'))
  const alignItems = computed(() => {
    const value = isColumn.value ? props.alignX : props.alignY
    return value ? alignment[value] : undefined
  })
  const justifyContent = computed(() => {
    const value = isColumn.value ? props.alignY : props.alignX
    return value ? alignment[value] : undefined
  })
  const resolvedPadding = computed(() => {
    if (props.padding !== undefined) return `${props.padding}px`
    if (props.paddingX === undefined && props.paddingY === undefined) return undefined
    return `${props.paddingY ?? 0}px ${props.paddingX ?? 0}px`
  })
  const resolvedStyle = computed<CSSProperties>(() => ({
    alignItems: alignItems.value,
    backgroundColor: props.bgColor ? `var(--${props.bgColor})` : undefined,
    borderRadius: props.radius === undefined ? undefined : `${props.radius}px`,
    flexBasis: props.flex === 'fluid' ? 0 : undefined,
    flexDirection: flexDirection.value as CSSProperties['flexDirection'],
    flexGrow: props.flex === 'fluid' ? 1 : undefined,
    flexWrap: props.wrap === 'no-wrap' ? 'nowrap' : props.wrap,
    gap: props.gap === undefined ? undefined : `${props.gap}px`,
    height: dimension(props.height),
    justifyContent: justifyContent.value,
    overflowY: props.overflowY,
    padding: resolvedPadding.value,
    width: dimension(props.width),
  }))
</script>

<style scoped>
  .ui-flex {
    display: flex;
    min-width: 0;
  }
</style>
